import { useEffect, useRef, useState } from "react";
import { Subscription } from "rxjs";
import { AssetPlayabilityResult } from "@shared/cmsAssetPlayability";
import { contentChannelService } from "@client/http/contentChannel.service";

const MAX_IDS_PER_REQUEST = 100;

export function useAssetPlayability(assetIds: string[]) {
  const [results, setResults] = useState<
    Record<string, AssetPlayabilityResult>
  >({});
  const [pending, setPending] = useState<string[]>([]);
  const requested = useRef(new Set<string>());

  const key = JSON.stringify(
    [...new Set(assetIds.map((id) => id.trim()).filter(Boolean))].sort()
  );

  useEffect(() => {
    const requestedIds = requested.current;
    const missing = (JSON.parse(key) as string[]).filter(
      (id) => !requestedIds.has(id)
    );
    if (!missing.length) return;

    const subscriptions: Subscription[] = [];
    const unfinished = new Set(missing);
    const settle = (ids: string[]) => {
      ids.forEach((id) => unfinished.delete(id));
      setPending((current) => current.filter((id) => !ids.includes(id)));
    };

    missing.forEach((id) => requestedIds.add(id));
    setPending((current) => [...current, ...missing]);

    for (let i = 0; i < missing.length; i += MAX_IDS_PER_REQUEST) {
      const chunk = missing.slice(i, i + MAX_IDS_PER_REQUEST);
      subscriptions.push(
        contentChannelService.getAssetPlayability(chunk).subscribe({
          next: (response) => {
            setResults((current) => ({
              ...current,
              ...Object.fromEntries(
                response.results.map((result) => [result.assetId, result])
              ),
            }));
            settle(chunk);
          },
          error: () => {
            chunk.forEach((id) => requestedIds.delete(id));
            settle(chunk);
          },
        })
      );
    }

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe());
      unfinished.forEach((id) => requestedIds.delete(id));
      setPending((current) => current.filter((id) => !unfinished.has(id)));
    };
  }, [key]);

  return {
    results,
    isPending: (assetId: string) => pending.includes(assetId.trim()),
  };
}
